import { PageInfo } from '../interfaces/layout/page-info.interface';

export interface ModusBreadcrumbItem {
  label: string;
  /** SPA navigation target — omit `url` so Modus renders a button, not an anchor. */
  route?: string;
  /** Full label when `label` is truncated for display. */
  tooltip?: string;
}

/** Standard GUID length (`3fa85f64-5717-4562-b3fc-2c963f66afa6`). */
export const BREADCRUMB_LABEL_MAX_LENGTH = 36;
const BREADCRUMB_LABEL_ELLIPSIS = '...';

export const REGISTERED_APPLICATIONS_BREADCRUMB_ROUTE = '/registered-applications';
export const REGISTERED_APPLICATIONS_BREADCRUMB_FALLBACK_LABEL = 'Registered applications';

const PROJECTS_ROOT_BREADCRUMB: ModusBreadcrumbItem = {
  label: 'Projects',
  route: '/projects',
};

export function truncateBreadcrumbLabel(
  label: string
): Pick<ModusBreadcrumbItem, 'label' | 'tooltip'> {
  if (label.length <= BREADCRUMB_LABEL_MAX_LENGTH) {
    return { label };
  }

  return {
    label: `${label.slice(0, BREADCRUMB_LABEL_MAX_LENGTH)}${BREADCRUMB_LABEL_ELLIPSIS}`,
    tooltip: label,
  };
}

function toModusCrumb(crumb: { label: string; route?: string }): ModusBreadcrumbItem {
  const item: ModusBreadcrumbItem = {
    ...truncateBreadcrumbLabel(crumb.label),
  };
  if (crumb.route) {
    item.route = crumb.route;
  }
  return item;
}

function resolveSupportRootCrumb(
  supportRoot?: Pick<ModusBreadcrumbItem, 'label' | 'route'> | null
): ModusBreadcrumbItem | null {
  const label = supportRoot?.label?.trim();
  const route = supportRoot?.route?.trim();
  if (!label || !route) {
    return null;
  }
  return toModusCrumb({ label, route });
}

export function buildModusBreadcrumbItems(
  pageInfo: Pick<PageInfo, 'breadcrumbs' | 'title'>,
  supportRoot?: Pick<ModusBreadcrumbItem, 'label' | 'route'> | null
): ModusBreadcrumbItem[] {
  const supportCrumb = resolveSupportRootCrumb(supportRoot);
  const onProjectsList =
    pageInfo.breadcrumbs.length === 0 && pageInfo.title === 'Projects';

  if (onProjectsList) {
    if (supportCrumb) {
      return [supportCrumb, { label: 'Projects' }];
    }
    return [{ label: 'Projects' }];
  }

  const items: ModusBreadcrumbItem[] = [];
  if (supportCrumb) {
    items.push(supportCrumb);
  }
  items.push(PROJECTS_ROOT_BREADCRUMB);

  for (const crumb of pageInfo.breadcrumbs) {
    items.push(toModusCrumb(crumb));
  }

  // Page title is shown in the nav bar center — only append it when there are no
  // layout crumbs (e.g. non-Projects root pages). When crumbs exist, the last
  // item is the current segment (Modus renders it as non-clickable).
  if (
    pageInfo.breadcrumbs.length === 0 &&
    pageInfo.title &&
    pageInfo.title !== 'Projects'
  ) {
    items.push(toModusCrumb({ label: pageInfo.title }));
  }

  return items;
}

export function getBreadcrumbNavigationTarget(
  crumb: ModusBreadcrumbItem
): string | null {
  return crumb.route ?? null;
}
